import React from "react";
import { Link } from "react-router-dom";
import { useLanguage, useTheme } from "../App";
import { Sun, Moon, Globe, ArrowLeft } from "lucide-react";

const CookiePolicy = () => {
  const { language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_saloneos-booking/artifacts/eysotq4t_smartbooker%20logo.png" 
                alt="SmartBooker" 
                className="h-8 w-auto"
              />
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={() => changeLanguage(language === "it" ? "en" : "it")}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                <Globe className="w-5 h-5" />
              </button>
              
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === "it" ? "Torna alla home" : "Back to home"}
          </Link>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <h1 className="text-3xl font-serif font-medium mb-2">Cookie Policy – SmartBooker</h1>
            <p className="text-muted-foreground mb-8">Ultimo aggiornamento: 13/03/2026</p>

            <p>
              La presente Cookie Policy descrive l'utilizzo dei cookie da parte della piattaforma 
              SmartBooker durante la navigazione sul sito web e sull'applicazione.
            </p>
            
            <p>
              Questa informativa è resa in conformità al Regolamento generale sulla protezione dei dati (GDPR) 
              e alla Direttiva ePrivacy.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">1. Cosa sono i cookie</h2>
            <p>
              I cookie sono piccoli file di testo che i siti web salvano sul dispositivo dell'utente 
              (computer, smartphone o tablet) durante la navigazione.
            </p>
            <p>
              I cookie permettono al sito di riconoscere il dispositivo dell'utente e di memorizzare 
              alcune informazioni utili per migliorare l'esperienza di utilizzo.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">2. Tipologie di cookie utilizzati</h2>
            <p>SmartBooker utilizza diverse categorie di cookie per garantire il corretto funzionamento della piattaforma.</p>

            <h3 className="text-lg font-medium mt-6 mb-3">Cookie tecnici (necessari)</h3>
            <p>Questi cookie sono indispensabili per il funzionamento del sito e della piattaforma.</p>
            <p>Consentono ad esempio di:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>effettuare il login alla piattaforma</li>
              <li>mantenere attiva la sessione utente</li>
              <li>gestire la sicurezza del sistema</li>
              <li>salvare preferenze di navigazione</li>
            </ul>
            <p className="mt-4 text-muted-foreground italic">Questi cookie non richiedono il consenso dell'utente.</p>

            <h3 className="text-lg font-medium mt-6 mb-3">Cookie di funzionalità</h3>
            <p>Questi cookie permettono di migliorare l'esperienza dell'utente salvando alcune preferenze, come:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>lingua selezionata</li>
              <li>impostazioni dell'interfaccia</li>
              <li>preferenze di utilizzo della piattaforma</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">Cookie analitici</h3>
            <p>Questi cookie raccolgono informazioni statistiche anonime sull'utilizzo del sito, ad esempio:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>pagine visitate</li>
              <li>durata della visita</li>
              <li>interazioni con il sito</li>
            </ul>
            <p className="mt-4">Servono per migliorare le prestazioni e l'usabilità della piattaforma.</p>
            <p>Quando possibile, tali dati vengono anonimizzati.</p>

            <h3 className="text-lg font-medium mt-6 mb-3">Cookie di terze parti</h3>
            <p>SmartBooker può utilizzare servizi forniti da terze parti che installano cookie sul dispositivo dell'utente.</p>
            <p>Questi servizi possono includere:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>strumenti di analisi del traffico</li>
              <li>servizi di sicurezza e protezione</li>
              <li>servizi di notifiche o messaggistica</li>
            </ul>
            <p className="mt-4">L'uso di tali cookie è regolato dalle privacy policy dei rispettivi fornitori.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">3. Gestione del consenso ai cookie</h2>
            <p>Quando l'utente visita il sito per la prima volta, viene mostrato un banner di consenso ai cookie che consente di:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>accettare tutti i cookie</li>
              <li>rifiutare i cookie non necessari</li>
              <li>personalizzare le preferenze</li>
            </ul>
            <p className="mt-4">Il consenso può essere modificato o revocato in qualsiasi momento.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">4. Come gestire o disabilitare i cookie</h2>
            <p>Gli utenti possono controllare o eliminare i cookie anche tramite le impostazioni del proprio browser.</p>
            <p>È possibile:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>bloccare i cookie</li>
              <li>eliminare i cookie salvati</li>
              <li>impostare avvisi quando un cookie viene installato</li>
            </ul>
            <p className="mt-4 text-amber-600 dark:text-amber-400">
              La disabilitazione dei cookie tecnici potrebbe compromettere il corretto funzionamento della piattaforma.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">5. Conservazione dei cookie</h2>
            <p>I cookie possono avere diverse durate:</p>
            
            <h3 className="text-lg font-medium mt-4 mb-2">Cookie di sessione</h3>
            <p>Vengono eliminati automaticamente alla chiusura del browser.</p>
            
            <h3 className="text-lg font-medium mt-4 mb-2">Cookie persistenti</h3>
            <p>Rimangono salvati sul dispositivo fino alla loro scadenza o cancellazione manuale.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">6. Titolare del trattamento</h2>
            <p>Il titolare del trattamento dei dati raccolti tramite cookie è:</p>
            <div className="bg-muted/50 p-4 rounded-lg my-4">
              <p className="mb-1"><strong>AleNic Service & Consulting di Antonio Luca</strong></p>
              <p className="mb-1">Titolare SmartBooker</p>
              <p className="mb-1">Indirizzo: Via Gaetano Martino 12/4 - 95036 Randazzo (CT)</p>
              <p>Email: <a href="mailto:info@smartbooker.it" className="text-accent">info@smartbooker.it</a></p>
            </div>

            <h2 className="text-xl font-semibold mt-8 mb-4">7. Modifiche alla Cookie Policy</h2>
            <p>SmartBooker può aggiornare la presente Cookie Policy in qualsiasi momento.</p>
            <p>Le modifiche saranno pubblicate su questa pagina con indicazione della data di aggiornamento.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">8. Contatti</h2>
            <p>Per qualsiasi informazione relativa alla presente Cookie Policy è possibile contattare:</p>
            <p className="mt-2">
              <strong>Email:</strong>{" "}
              <a href="mailto:info@smartbooker.it" className="text-accent">info@smartbooker.it</a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            © 2026 SmartBooker - Powered by AleNic Service and Consulting
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicy;
